import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { getSetProgress } from "./collectionApi";
import { getPokemonCards } from "../cards/services/pokemonAPI";
import { useCollection } from "../cards/collectionProvider/CollectionProvider";
import { useWishlist } from "../wishlist/providers/WishlistProvider";
import Card from "../cards/components/card/Card";
import CardGridSkeleton from "../cards/components/card/CardGridSkeleton";
import Loader from "../styles/Loader";
import { useDismissible } from "../native/dismissibleStack";

const TIER_COLORS = {
  complete: "#10b981",
  high: "#6b21a8",
  mid: "#2563eb",
  low: "#94a3b8",
};

// "<1" rather than "0" once anything is owned: 1 of 295 rounds to 0, and "0%"
// next to "1 / 295" reads as nothing collected (tablet QA, versionCode 11).
export const percentLabel = (set) =>
  set && set.owned > 0 && set.percentage < 1 ? "<1" : String(set?.percentage ?? 0);

const getTier = (pct) => {
  if (pct >= 100) return "complete";
  if (pct >= 60) return "high";
  if (pct >= 20) return "mid";
  return "low";
};

const SetCompletionView = () => {
  const { collectionIds } = useCollection();
  const { toggleWishlist, isWishlisted, addBulkToWishlist, wishlistIds } = useWishlist();

  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [detailSetId, setDetailSetId] = useState(null);
  const [detailCards, setDetailCards] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  // A failed card list is not an empty set (code review ERRORS-STATES-12).
  const [detailFailed, setDetailFailed] = useState(false);
  const [addingMissing, setAddingMissing] = useState(false);

  const loadProgress = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSetProgress();
      setSets(data);
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProgress(); }, [loadProgress]);

  const detailSet = useMemo(
    () => sets.find((s) => s.setId === detailSetId),
    [sets, detailSetId],
  );

  // Sequence number of the most recently requested set detail. getPokemonCards
  // pages through a set sequentially, so a big set's response can land after a
  // smaller set opened later has already answered; without this token the late
  // response would overwrite detailCards, painting the abandoned set's grid
  // under the open set's header and pointing the bulk-wishlist button at the
  // wrong set's cards.
  const detailRequestRef = useRef(0);

  const openSetDetail = useCallback(async (setId) => {
    const requestId = ++detailRequestRef.current;
    setDetailSetId(setId);
    setDetailLoading(true);
    setDetailFailed(false);
    try {
      const cards = await getPokemonCards(setId);
      // Superseded by a newer open (or by backing out): drop the response, and
      // leave the loading flag to whoever owns it now.
      if (detailRequestRef.current !== requestId) return;
      setDetailCards(cards);
    } catch {
      if (detailRequestRef.current !== requestId) return;
      setDetailCards([]);
      setDetailFailed(true);
    } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  }, []);

  // Backing out abandons the in-flight request too: bump the token so its
  // response cannot refill the grid we are clearing here, and clear the
  // loading flag it will no longer clear itself.
  const closeSetDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setDetailSetId(null);
    setDetailCards([]);
    setDetailLoading(false);
  }, []);

  // An open set is a step INTO the Sets tab, not a route, so Android Back went
  // straight past it and left Collection (tablet QA, versionCode 11). Back now
  // returns to the set list, as "All Sets" does.
  useDismissible(!!detailSetId, closeSetDetail, "collection-set-detail");

  const missingCardIds = useMemo(() => {
    if (!detailCards.length) return [];
    return detailCards
      .filter((c) => !collectionIds.has(c._id?.toString()))
      .map((c) => c._id?.toString())
      .filter(Boolean);
  }, [detailCards, collectionIds]);

  const unwishlistedMissingIds = useMemo(
    () => missingCardIds.filter((id) => !wishlistIds.has(id)),
    [missingCardIds, wishlistIds],
  );

  const handleAddAllMissing = useCallback(async () => {
    if (unwishlistedMissingIds.length === 0) return;
    setAddingMissing(true);
    await addBulkToWishlist(unwishlistedMissingIds);
    setAddingMissing(false);
  }, [unwishlistedMissingIds, addBulkToWishlist]);

  // ── Set detail view ──
  if (detailSetId) {
    return (
      <div>
        <button
          type="button"
          onClick={closeSetDetail}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "none", border: "none", cursor: "pointer",
            padding: "0 0 16px", color: "#6b21a8", fontSize: 13, fontWeight: 800,
            fontFamily: "Inter, sans-serif",
          }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          All Sets
        </button>

        {detailSet && (
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
              {detailSet.setName}
            </h2>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b", fontWeight: 500 }}>
              {detailSet.owned} / {detailSet.total} collected · {percentLabel(detailSet)}%
              {detailSet.series && ` · ${detailSet.series}`}
            </p>
            {/* Progress bar */}
            <div style={{
              marginTop: 10, height: 6, borderRadius: 3,
              background: "rgba(0,0,0,0.06)", overflow: "hidden",
            }}>
              <div style={{
                height: "100%", borderRadius: 3,
                width: `${detailSet.percentage}%`,
                background: TIER_COLORS[getTier(detailSet.percentage)],
                transition: "width 0.4s ease",
              }} />
            </div>
          </div>
        )}

        {/* Bulk wishlist button */}
        {!detailLoading && unwishlistedMissingIds.length > 0 && (
          <button
            type="button"
            onClick={handleAddAllMissing}
            disabled={addingMissing}
            style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              width: "100%", padding: "12px 16px", marginBottom: 16,
              background: addingMissing ? "rgba(124,58,237,0.5)" : "linear-gradient(135deg, #7c3aed, #6b21a8)",
              border: "none", borderRadius: 14, cursor: addingMissing ? "default" : "pointer",
              color: "#fff", fontSize: 14, fontWeight: 800,
              fontFamily: "Inter, sans-serif",
              boxShadow: "0 4px 16px rgba(107,33,168,0.35)",
            }}
          >
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            {addingMissing
              ? "Adding..."
              : `Add all ${unwishlistedMissingIds.length} missing to wishlist`}
          </button>
        )}

        {detailLoading ? (
          <CardGridSkeleton count={6} />
        ) : detailFailed ? (
          <div data-testid="set-detail-load-failed" style={{ textAlign: "center", color: "#475569", fontWeight: 600, padding: "40px 0" }}>
            <p style={{ margin: 0 }}>Couldn't load this set's cards.</p>
            <button
              type="button"
              onClick={() => openSetDetail(detailSetId)}
              style={{ marginTop: 10, padding: "8px 14px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#fff", fontWeight: 700, cursor: "pointer" }}
            >
              Try again
            </button>
          </div>
        ) : detailCards.length === 0 ? (
          <p style={{ textAlign: "center", color: "#64748b", fontWeight: 600, padding: "40px 0" }}>
            No cards found for this set
          </p>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
            {detailCards.map((card) => {
              const owned = collectionIds.has(card._id?.toString());
              const wishlisted = isWishlisted(card._id);
              return (
                <div
                  key={card._id}
                  style={{
                    opacity: owned ? 1 : 0.55,
                    position: "relative",
                  }}
                >
                  <Card
                    pokemonCard={card}
                    inCollection={owned}
                    isWishlisted={wishlisted}
                    onToggleWishlist={owned ? undefined : toggleWishlist}
                  />
                  {!owned && (
                    <div style={{
                      position: "absolute", top: 8, right: 8, zIndex: 10,
                      background: wishlisted ? "#ef4444" : "rgba(0,0,0,0.5)",
                      borderRadius: 8, padding: "3px 8px",
                      fontSize: 9, fontWeight: 900, color: "#fff",
                      textTransform: "uppercase", letterSpacing: "0.06em",
                    }}>
                      {wishlisted ? "Wanted" : "Missing"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Set list view ──
  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
        <Loader context="grid" label="Loading sets" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "40px 0" }}>
        <p style={{ color: "#ef4444", fontWeight: 700, fontSize: 14 }}>{error}</p>
        <button onClick={loadProgress} style={{
          marginTop: 12, background: "#6b21a8", color: "#fff", border: "none",
          borderRadius: 12, padding: "10px 20px", fontWeight: 800, cursor: "pointer",
        }}>
          Retry
        </button>
      </div>
    );
  }

  if (sets.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0", color: "#64748b" }}>
        <p style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>No sets available</p>
      </div>
    );
  }

  const setsWithOwned = sets.filter((s) => s.owned > 0);
  const setsWithout = sets.filter((s) => s.owned === 0);

  return (
    <div>
      {setsWithOwned.length > 0 && (
        <>
          <p style={{ margin: "0 0 12px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Your Sets ({setsWithOwned.length})
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {setsWithOwned.map((s) => (
              <SetProgressBar key={s.setId} set={s} onTap={openSetDetail} />
            ))}
          </div>
        </>
      )}

      {setsWithout.length > 0 && (
        <>
          <p style={{ margin: "0 0 12px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Not Started ({setsWithout.length})
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {setsWithout.map((s) => (
              <SetProgressBar key={s.setId} set={s} onTap={openSetDetail} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const SetProgressBar = ({ set, onTap }) => {
  const tier = getTier(set.percentage);
  const color = TIER_COLORS[tier];
  return (
    <button
      type="button"
      onClick={() => onTap(set.setId)}
      style={{
        display: "block", width: "100%", textAlign: "left",
        background: "#fff", border: "1.5px solid rgba(0,0,0,0.06)",
        borderRadius: 16, padding: "14px 16px", cursor: "pointer",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        transition: "all 0.15s ease",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {set.setName}
          </div>
          {set.series && (
            <div style={{ fontSize: 11, fontWeight: 500, color: "#64748b", marginTop: 1 }}>
              {set.series}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "baseline", gap: 4, flexShrink: 0, marginLeft: 12 }}>
          <span style={{ fontSize: 18, fontWeight: 900, color: tier === "low" ? "#64748b" : color }}>
            {set.owned}
          </span>
          <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
            / {set.total}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        height: 6, borderRadius: 3,
        background: "rgba(0,0,0,0.06)", overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: 3,
          width: `${Math.max(set.percentage, set.owned > 0 ? 2 : 0)}%`,
          background: color,
          transition: "width 0.4s ease",
        }} />
      </div>

      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginTop: 6,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color }}>
          {percentLabel(set)}%
        </span>
        {tier === "complete" && (
          <span style={{ fontSize: 10, fontWeight: 900, color: "#10b981", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Complete
          </span>
        )}
      </div>
    </button>
  );
};

export default SetCompletionView;
